import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Spin,
  Table,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import API from "../../../services/api-request";
import { capitalizeLetter } from "../../../utils/capitalizeLetter";
import { getDataLogin } from "../../../utils/getDataLogin";

const renderExpandedRow = (record, columnsDetailpallets) => (
  <Table
    columns={columnsDetailpallets}
    dataSource={record.transaction_detail_pallets}
    rowKey={(dataSource) => dataSource.id}
    pagination={false}
  />
);

const renderTableSummary = (pageData, columnsProducts) => {
  const totalEstimateWeight = pageData.reduce(
    (sum, item) => sum + (item.estimate_weight || 0),
    0,
  );
  const totalEstimateQty = pageData.reduce(
    (sum, item) => sum + (item.estimate_qty || 0),
    0,
  );

  return (
    <Table.Summary.Row>
      <Table.Summary.Cell>
        <span style={{ fontWeight: "bold" }}>Total</span>
      </Table.Summary.Cell>
      {columnsProducts.map((column, index) => {
        if (column.dataIndex === "estimate_weight") {
          return (
            <Table.Summary.Cell key={column.key} index={index}>
              <span style={{ fontWeight: "bold" }}>
                {totalEstimateWeight?.toLocaleString("id-ID", {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}{" "}
                kg
              </span>
            </Table.Summary.Cell>
          );
        }

        if (column.dataIndex === "estimate_qty") {
          return (
            <Table.Summary.Cell key={column.key} index={index}>
              <span style={{ fontWeight: "bold" }}>{totalEstimateQty}</span>
            </Table.Summary.Cell>
          );
        }

        return <Table.Summary.Cell key={column.key} index={index} />;
      })}
    </Table.Summary.Row>
  );
};

const getButtonSpan = (status, isNeedChecked) => {
  if (status === "Pending") return 8;
  if (isNeedChecked) return 12;
  return 24;
};

const DetailInbound = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [editForm] = Form.useForm();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  const [note, setNote] = useState("");

  const [open, setOpen] = useState(false);
  const [openStatus, setOpenStatus] = useState("approved");
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [openCheck, setOpenCheck] = useState(false);

  const isNeedChecked =
    data?.status === "Done" &&
    getDataLogin()?.privilege?.name?.toLowerCase() === "management";

  const getDataDetail = useCallback((id) => {
    setLoading(true);
    API.Get(`transaction/inbound/detail/${id}`)
      .then((result) => {
        setLoading(false);

        const transformedItem = {
          ...result?.data,
          status: capitalizeLetter(result?.data?.status),
          client: result?.data?.client?.name,
          request_at: dayjs(result?.data?.request_at).format("YYYY-MM-DD"),
          schedule_at: dayjs(result?.data?.schedule_at).format("YYYY-MM-DD"),
          material_handling_fee_type: result?.data?.material_handling_fee_type
            ? capitalizeLetter(result?.data?.material_handling_fee_type)
            : null,
        };

        editForm.setFieldsValue(transformedItem);

        setData(transformedItem);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        message.info("Failed Fetch Request Inbound Detail");
      });
  }, [editForm]);

  useEffect(() => {
    if (id) {
      getDataDetail(id);
    }
  }, [id, getDataDetail]);

  const onFinish = async (values) => { };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const showModal = (status) => {
    setOpenStatus(status);
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleOk = async () => {
    if (openStatus === "declined" && !note) {
      return message.info("Alasan Wajib diisi");
    }

    setConfirmLoading(true);

    try {
      let dataForm = {
        id: id,
        reason: note,
      };

      await API.Post(
        openStatus === "declined"
          ? "transaction/inbound/reject"
          : "transaction/inbound/approve",
        dataForm,
      );

      message.success(
        `Inbound has been ${openStatus === "declined" ? "Rejected" : "Approved"
        }`,
      );
      setOpen(false);
      navigate(-1);
    } catch (err) {
      console.error(err);
      message.info(
        `Failed to ${openStatus === "declined" ? "Reject" : "Approve"} Inbound`,
      );
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleOkCheck = async () => {
    setConfirmLoading(true);

    try {
      let dataForm = {
        id: id,
      };

      await API.Post("transaction/inbound/checked", dataForm);

      message.success(`Inbound has been Checked`);
      setOpenCheck(false);
      navigate(-1);
    } catch (err) {
      console.error(err);
      message.info(`Failed to Check Inbound`);
    } finally {
      setConfirmLoading(false);
    }
  };

  const columnsProducts = [
    {
      title: "No",
      dataIndex: "no",
      key: "no",
      width: 60,
      render: (text, record, index) => index + 1,
    },
    {
      title: "Jenis Produk",
      dataIndex: "product",
      key: "product",
      sorter: (a, b) => a.product?.name - b.product?.name,
      render: (product) => {
        if (product?.name === null || product?.name === "") {
          return "-";
        } else {
          return product?.name;
        }
      },
    },
    {
      title: "Berat",
      dataIndex: "estimate_weight",
      key: "estimate_weight",
      sorter: (a, b) => a.estimate_weight - b.estimate_weight,
      render: (estimate_weight) => {
        if (estimate_weight === null || estimate_weight === "") {
          return "-";
        } else {
          return `${Math.round(estimate_weight).toLocaleString("id-ID")} Kg`;
        }
      },
    },
    {
      title: "Jumlah Pack",
      dataIndex: "estimate_qty",
      key: "estimate_qty",
      sorter: (a, b) => a.estimate_qty - b.estimate_qty,
      render: (estimate_qty) => {
        if (estimate_qty === null || estimate_qty === "") {
          return "-";
        } else {
          return estimate_qty;
        }
      },
    },
    {
      title: "Spesifikasi",
      dataIndex: "estimate_specification",
      key: "estimate_specification",
      sorter: (a, b) => a.estimate_specification - b.estimate_specification,
      render: (estimate_specification) => {
        if (estimate_specification === null || estimate_specification === "") {
          return "-";
        } else {
          return estimate_specification;
        }
      },
    },
  ];

  const columnsDetailpallets = [
    {
      title: "Pallet",
      dataIndex: "pallet",
      key: "pallet",
      sorter: (a, b) => a.pallet?.name - b.pallet?.name,
      render: (pallet) => {
        if (pallet === null || pallet === "") {
          return "-";
        } else {
          return pallet?.name;
        }
      },
    },
    {
      title: "Tally",
      dataIndex: "tally",
      key: "tally",
      sorter: (a, b) => a.tally?.name - b.tally?.name,
      render: (tally) => {
        if (!tally) {
          return "-";
        } else {
          return tally?.name;
        }
      },
    },
    {
      title: "Operator",
      dataIndex: "operator",
      key: "operator",
      sorter: (a, b) => a.operator?.name - b.operator?.name,
      render: (operator) => {
        if (operator === null || operator === "") {
          return "-";
        } else {
          return operator?.name;
        }
      },
    },
    {
      title: "Berat",
      dataIndex: "weight",
      key: "weight",
      sorter: (a, b) => a.weight - b.weight,
      render: (weight) => {
        if (weight === null || weight === "") {
          return "-";
        } else {
          return `${Math.round(weight).toLocaleString("id-ID")} Kg`;
        }
      },
    },
    {
      title: "Spesifikasi",
      dataIndex: "specification",
      key: "specification",
      sorter: (a, b) => a.specification - b.specification,
      render: (specification) => {
        if (specification === null || specification === "") {
          return "-";
        } else {
          return specification;
        }
      },
    },
    {
      title: "Jumlah Pack",
      dataIndex: "qty",
      key: "qty",
      sorter: (a, b) => a.qty - b.qty,
      render: (qty) => {
        if (qty === null || qty === "") {
          return "-";
        } else {
          return qty;
        }
      },
    },
    {
      title: "Cold Storage",
      dataIndex: "area",
      key: "area",
      sorter: (a, b) => a.area?.warehouse?.n - b.area?.warehouse?.n,
      render: (area) => {
        if (area === null || area === "") {
          return "-";
        } else {
          return area?.warehouse?.n;
        }
      },
    },
    {
      title: "Area",
      dataIndex: "area",
      key: "area",
      sorter: (a, b) => a.area?.name - b.area?.name,
      render: (area) => {
        if (area === null || area === "") {
          return "-";
        } else {
          return area?.name;
        }
      },
    },
  ];

  return (
    <>
      {loading ? (
        <div
          style={{
            width: "100%",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin />
        </div>
      ) : (
        <Form
          form={editForm}
          name="Edit Form"
          style={{
            margin: "0 auto",
          }}
          layout="vertical"
          autoComplete="off"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
        >
          <Row gutter={[8, 8]}>
            <Col lg={12} md={12} sm={12} xs={24}>
              <Form.Item label="Kode" name="code">
                <Input placeholder="Kode" readOnly />
              </Form.Item>
            </Col>
            <Col lg={6} md={6} sm={6} xs={24}>
              <Form.Item
                label="Material Handling"
                name="material_handling_fee_type"
              >
                <Input placeholder="Material Handling" readOnly />
              </Form.Item>
            </Col>
            <Col lg={6} md={6} sm={6} xs={24}>
              <Form.Item
                label="Material Handling Fee per kg"
                name="material_handling_fee_cost"
              >
                <InputNumber
                  placeholder="Material Handling Fee"
                  prefix="Rp"
                  min={0}
                  controls={false}
                  style={{
                    width: "100%",
                  }}
                  readOnly
                />
              </Form.Item>
            </Col>
            <Col lg={12} md={12} sm={12} xs={24}>
              <Form.Item
                label="Pemilik"
                name="client"
                rules={[
                  {
                    required: true,
                    message: "Please input your Pemilik!",
                  },
                ]}
              >
                <Input placeholder="Pemilik" readOnly />
              </Form.Item>
            </Col>
            <Col lg={12} md={12} sm={12} xs={24}>
              <Form.Item
                label="Status"
                name="status"
                rules={[
                  {
                    required: true,
                    message: "Please input your Status!",
                  },
                ]}
              >
                <Input placeholder="Status" readOnly />
              </Form.Item>
            </Col>
            {data.reason ? (
              <Col lg={12} md={12} sm={12} xs={24}>
                <Form.Item
                  label="Alasan Penolakan"
                  name="reason"
                  rules={[
                    {
                      required: true,
                      message: "Please input your Alasan!",
                    },
                  ]}
                >
                  <Input placeholder="Alasan" readOnly />
                </Form.Item>
              </Col>
            ) : null}
            <Col lg={12} md={12} sm={12} xs={24}>
              <Form.Item
                label="Tanggal Request"
                name="request_at"
                rules={[
                  {
                    required: true,
                    message: "Please input your Tanggal Request!",
                  },
                ]}
              >
                <Input placeholder="Tanggal Request" readOnly />
              </Form.Item>
            </Col>
            <Col lg={12} md={12} sm={12} xs={24}>
              <Form.Item
                label="Tanggal Masuk"
                name="schedule_at"
                rules={[
                  {
                    required: true,
                    message: "Please input your Tanggal Masuk!",
                  },
                ]}
              >
                <Input placeholder="Tanggal Masuk" readOnly />
              </Form.Item>
            </Col>
            <Col lg={24} md={24} sm={24} xs={24}>
              <div style={{ fontWeight: 600, marginBottom: 10 }}>
                Jenis Produk & Berat/Jumlah Pack
              </div>
            </Col>
            <Col lg={24} md={24} sm={24} xs={24}>
              <Table
                dataSource={data.transaction_details}
                columns={columnsProducts}
                rowKey={(dataSource) => dataSource.id}
                expandable={{
                  expandedRowRender: (record) =>
                    renderExpandedRow(record, columnsDetailpallets),
                }}
                summary={(pageData) => renderTableSummary(pageData, columnsProducts)}
                pagination={false}
              />
            </Col>
            {/* <Col lg={24} md={24} sm={24} xs={24}>
              <Form.Item label="Keterangan" name="note">
                <TextArea
                  placeholder="Keterangan"
                  autoSize={{
                    minRows: 2,
                    maxRows: 6,
                  }}
                />
              </Form.Item>
            </Col> */}
            <Col
              lg={getButtonSpan(data.status, isNeedChecked)}
              md={getButtonSpan(data.status, isNeedChecked)}
              sm={getButtonSpan(data.status, isNeedChecked)}
              xs={24}
            >
              <Button
                style={{
                  width: "100%",
                  backgroundColor: "orange",
                  borderColor: "orange",
                  color: "white",
                }}
                type="primary"
                onClick={() => handleBack()}
              >
                Kembali
              </Button>
            </Col>
            {data.status === "Pending" ? (
              <>
                <Col lg={8} md={8} sm={8} xs={24}>
                  <Button
                    htmlType="submit"
                    style={buttonStyle}
                    type="default"
                    danger
                    onClick={() => showModal("declined")}
                  >
                    Reject
                  </Button>
                </Col>
                <Col lg={8} md={8} sm={8} xs={24}>
                  <Button
                    htmlType="submit"
                    style={buttonStyle}
                    type="primary"
                    onClick={() => showModal("approved")}
                  >
                    Approve
                  </Button>
                </Col>
              </>
            ) : null}
            {isNeedChecked && (
              <Col lg={12} md={12} sm={12} xs={24}>
                <Button
                  htmlType="submit"
                  style={buttonStyle}
                  type="primary"
                  onClick={() => setOpenCheck(true)}
                >
                  Check
                </Button>
              </Col>
            )}
          </Row>
        </Form>
      )}
      <Modal
        title="Konfirmasi"
        okText={openStatus === "declined" ? "Reject" : "Approve"}
        cancelText="Batal"
        open={open}
        onOk={handleOk}
        confirmLoading={confirmLoading}
        onCancel={handleCancel}
        okType={"primary"}
        okButtonProps={{ danger: openStatus === "declined" }}
      >
        <p>
          {openStatus === "declined"
            ? `Apakah anda akan menolak permohonan ini?`
            : `Apakah anda akan menyetujui permohonan ini?`}
        </p>
        {openStatus === "declined" ? (
          <TextArea
            placeholder="Masukan Alasan"
            autoSize={{
              minRows: 2,
              maxRows: 6,
            }}
            onChange={(e) => setNote(e.target.value)}
          />
        ) : null}
      </Modal>
      <Modal
        title="Konfirmasi"
        okText={"Simpan"}
        cancelText="Batal"
        open={openCheck}
        onOk={handleOkCheck}
        confirmLoading={confirmLoading}
        onCancel={() => setOpenCheck(false)}
        okType={"primary"}
      >
        <p>
          Anda telah melakukan pengecekan Inbound. Apakah ingin menyimpan data?
        </p>
      </Modal>
    </>
  );
};

const buttonStyle = {
  width: "100%",
};


DetailInbound.propTypes = {

};

export default DetailInbound;
